import React, { useState } from 'react';
import { Box, Typography, IconButton, Tooltip, Stack, Pagination, Button, PaginationItem } from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import AnimatedPage from '../../components/AnimatedPage';
import TransactionFiltersComponent from './components/TransactionFilters';
import TransactionsTable from './components/TransactionsTable';
import TransactionDetailsDialog from './components/TransactionDetailsDialog';
import { useTransactions } from './hooks/useTransactions';
import { transactionsService } from '../../services';
import { useNotification } from '../../contexts/NotificationContext';

export default function Transactions() {
  const {
    transactions,
    merchants,
    traders,
    paymentSystems,
    error,
    loading,
    selectedTransaction,
    detailsDialogOpen,
    totalPages,
    filters,
    setDetailsDialogOpen,
    setSelectedTransaction,
    handleFilterChange,
    handleSearch,
    handleReset,
    handlePageChange,
    handleViewDetails,
    loadData,
    handleRefresh,
    handleToggleIncludeNotFound,
  } = useTransactions();
  const { showNotification } = useNotification();
  const [isExporting, setIsExporting] = useState(false);
  const isTraderSelected = !!filters.trader_id;
  const areExportDatesSelected = !!filters.start_date && !!filters.end_date;
  const canExportTransactions = isTraderSelected || areExportDatesSelected;
  const exportRequirementMessage =
    'Выберите трейдера или укажите начальную и конечную даты для выгрузки транзакций';

  const handleExport = async (format: 'csv' | 'xlsx') => {
    if (isExporting) return;
    if (!canExportTransactions) {
      showNotification(exportRequirementMessage, 'error');
      return;
    }

    try {
      setIsExporting(true);

      const params: any = {
        format,
        merchant_id: filters.merchant_id || undefined,
        trader_id: filters.trader_id || undefined,
        payment_system_id: filters.payment_system_id || undefined,
        currency: filters.currency || undefined,
        status: filters.status || undefined,
        search_id: filters.search_id || undefined,
        start_date: filters.start_date || undefined,
        end_date: filters.end_date || undefined,
      };

      const response = await transactionsService.exportTransactions(params);
      const contentType =
        response.headers['content-type'] ||
        (format === 'xlsx'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'text/csv;charset=utf-8');
      const blob = new Blob([response.data], { type: contentType });

      const disposition = response.headers['content-disposition'];
      const filenameMatch = disposition?.match(/filename\*?=(?:UTF-8''|")?([^\";\n]+)/i);
      const decodedFilename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1].replace(/"/g, ''))
        : `transactions.${format}`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = decodedFilename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification('Файл успешно выгружен', 'success');
    } catch (err: any) {
      console.error('Error exporting transactions:', err);
      showNotification('Ошибка при выгрузке файла', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>Транзакции</Typography>
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              onClick={handleRefresh}
              disabled={loading}
              startIcon={<RefreshIcon />}
            >
              Обновить
            </Button>
            <Tooltip
              title={canExportTransactions ? '' : exportRequirementMessage}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!canExportTransactions || isExporting}
                  onClick={() => handleExport('xlsx')}
                >
                  Выгрузить XLSX
                </Button>
              </span>
            </Tooltip>
            <Tooltip
              title={canExportTransactions ? '' : exportRequirementMessage}
            >
              <span>
                <Button
                  variant="contained"
                  disabled={!canExportTransactions || isExporting}
                  onClick={() => handleExport('csv')}
                >
                  Выгрузить CSV
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ mb: 3 }}>
          <TransactionFiltersComponent
            filters={filters}
            merchants={merchants}
            traders={traders}
            paymentSystems={paymentSystems}
            onFilterChange={handleFilterChange}
            onSearch={handleSearch}
            onReset={handleReset}
            onToggleIncludeNotFound={handleToggleIncludeNotFound}
          />
        </Box>

        <TransactionsTable
          transactions={transactions}
          merchants={merchants}
          traders={traders}
          loading={loading}
          error={error}
          onViewDetails={handleViewDetails}
        />

        {totalPages > 1 && (
          <Stack spacing={2} alignItems="center" sx={{ mt: 3 }}>
            <Pagination
              count={totalPages}
              page={filters.page}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  disabled={item.page === filters.page}
                />
              )}
            />
          </Stack>
        )}

        <TransactionDetailsDialog
          transaction={selectedTransaction}
          open={detailsDialogOpen}
          onClose={() => setDetailsDialogOpen(false)}
          merchants={merchants}
          traders={traders}
          onUpdate={() => setDetailsDialogOpen(false)}
          loadData={loadData}
        />
      </Box>
    </AnimatedPage>
  );
}
